/**
 * Client-side trip access checks for cached / hydrated trip payloads.
 * Must align with Firestore rules (owner + members + memberIds) and ignore collaborators who left.
 */
export function collaboratorHasNotLeft(collaborator: {
  userId?: string;
  leftAt?: unknown;
} | null | undefined): boolean {
  if (!collaborator?.userId) {
    return false;
  }
  return collaborator.leftAt == null || collaborator.leftAt === '';
}

export function userIsFormerMemberOnly(
  uid: string,
  trip: {
    owner?: string;
    members?: unknown;
    memberIds?: unknown;
    formerMembers?: Array<{ userId?: string }> | null;
  },
): boolean {
  if (!uid || trip.owner === uid) {
    return false;
  }
  const members = Array.isArray(trip.members) ? trip.members : [];
  const memberIds = Array.isArray(trip.memberIds) ? trip.memberIds : [];
  if (members.includes(uid) || memberIds.includes(uid)) {
    return false;
  }
  return (
    Array.isArray(trip.formerMembers) &&
    trip.formerMembers.some((member) => member?.userId === uid)
  );
}

export function canUserAccessTripFromStoredData(
  uid: string,
  trip: {
    owner?: string;
    members?: unknown;
    memberIds?: unknown;
    formerMembers?: Array<{ userId?: string }> | null;
    collaborators?: Array<{ userId?: string; leftAt?: unknown }> | null;
  },
): boolean {
  if (!uid) {
    return false;
  }
  if (trip.owner === uid) {
    return true;
  }
  const members = Array.isArray(trip.members) ? trip.members : [];
  const memberIds = Array.isArray(trip.memberIds) ? trip.memberIds : [];
  // Server-synced membership arrays are authoritative (Firestore rules use these for list/read).
  if (members.includes(uid) || memberIds.includes(uid)) {
    return true;
  }
  if (userIsFormerMemberOnly(uid, trip)) {
    return false;
  }
  if (Array.isArray(trip.collaborators)) {
    const rosterEntry = trip.collaborators.find((collaborator) => collaborator?.userId === uid);
    if (rosterEntry && !collaboratorHasNotLeft(rosterEntry)) {
      return false;
    }
  }
  if (Array.isArray(trip.collaborators)) {
    return trip.collaborators.some(
      (collaborator) => collaborator?.userId === uid && collaboratorHasNotLeft(collaborator),
    );
  }
  return false;
}

type TripAccessShape = {
  owner?: string;
  members?: unknown;
  memberIds?: unknown;
  formerMembers?: Array<{ userId?: string }> | null;
  collaborators?: Array<{ userId?: string; leftAt?: unknown }> | null;
};

/** Drop trips the current user must not see (left, former member, or stale roster). */
export function filterTripsByUserAccess<T extends TripAccessShape>(uid: string, trips: T[]): T[] {
  if (!uid) {
    return [];
  }
  return trips.filter((trip) => canUserAccessTripFromStoredData(uid, trip));
}
